import React from 'react';
import { useSnackbar } from '@/alerts/index';
import { useAccountListsV2 } from '@/dashboard/AccountListsV2/hooks/useAccountListsV2';
import {
  LizanoListInfo,
  LizanoListProperty,
} from '@/reports/sections/ListsSection/lists/lists';
import { Text, Button, IconButton, Icons, Stack } from '@/theme/index';
import { pluralizeString, useScreenSize } from '@/utils/index';

type DeleteListItemsActionModalBodyProps = {
  data: LizanoListProperty[];
  currentList: LizanoListInfo;
  closeModal: () => void;
  onSuccess: () => void;
};

export const DeleteListItemsActionModalBody = ({
  data,
  currentList,
  closeModal,
  onSuccess,
}: DeleteListItemsActionModalBodyProps) => {
  const { bulkDeleteListProperty } = useAccountListsV2();
  const { isMobile } = useScreenSize();
  const { enqueueSnackbar } = useSnackbar();

  const handleDeleteItem = () => {
    if (data.length === 0) return;

    bulkDeleteListProperty.mutate(
      {
        list_code: currentList.list_code,
        propertyIDs: data.map((report) => `${report.id}`),
      },
      {
        onSuccess: () => {
          onSuccess();
          closeModal();
          enqueueSnackbar(
            `Deleted ${data.length} ${pluralizeString('item', data.length)}`,
            {
              variant: 'success',
            }
          );
        },
      }
    );
  };

  const pluralizedStringReport = pluralizeString('report', data.length);

  return (
    <Stack
      direction="column"
      padding={(theme) => (isMobile ? '' : theme.spacing(2, 2.5))}
      width={isMobile ? 'initial' : '320px'}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Text variant="h3" mb={(theme) => theme.spacing(2.5)}>
          Delete
        </Text>
        <IconButton aria-label="Close" onClick={closeModal}>
          <Icons.Close />
        </IconButton>
      </Stack>
      <Text>
        Are you sure want to delete the selected {pluralizedStringReport}? The{' '}
        {pluralizedStringReport} will be removed from the list but will still be
        accessible via search or recent reports.
      </Text>
      <Stack
        direction="row"
        justifyContent="flex-end"
        mt={(theme) => theme.spacing(2.5)}
      >
        <Button
          variant="text"
          color="success"
          sx={{ fontSize: '0.875rem', textDecoration: 'underline', mr: 2.5 }}
          onClick={closeModal}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={() => handleDeleteItem()}
          sx={{ fontSize: '0.875rem' }}
        >
          Delete
        </Button>
      </Stack>
    </Stack>
  );
};
